/**
 * Draw SVG text-underlines once as they enter the viewport,
 * after any scroll-reveal on an ancestor has finished.
 * Siblings that appear together are staggered in reading order.
 *
 * Safari will not paint a mask on each line of a wrapped inline, so a span
 * that breaks is split into one nowrap span per line.
 */
(function () {
	'use strict';

	const DURATION_FALLBACK = 700;
	const STAGGER_MS = 540;
	const LINE_STAGGER_MS = 180;
	const BATCH_MS = 80;
	const SELECTOR = '.entry-content span[style*="underline"], .site-main span[style*="underline"]';
	const batches = new Map();
	const signatures = new Map();
	let hosts = [];
	let observer = null;
	let layoutTimer = null;
	let layoutForce = false;
	const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	function initTextUnderlines() {
		hosts = Array.prototype.filter.call(document.querySelectorAll(SELECTOR), function (span) {
			return !span.classList.contains('text-underline-line');
		});

		if (!hosts.length) {
			return;
		}

		hosts.forEach(function (span) {
			if (span.dataset.underlineSource == null) {
				span.dataset.underlineSource = span.innerHTML;
			}
		});

		layout(true);
		watchReflow();

		if (document.fonts && document.fonts.ready) {
			document.fonts.ready.then(function () {
				scheduleLayout(true);
			});
		}
	}

	function watchReflow() {
		// The parent is often an inline strong, which Safari won't observe.
		window.addEventListener('resize', function () {
			scheduleLayout(false);
		});

		if (!window.ResizeObserver) {
			return;
		}

		const seen = new Set();
		const ro = new ResizeObserver(function () {
			scheduleLayout(false);
		});

		hosts.forEach(function (span) {
			const block = layoutBox(span);

			if (block && !seen.has(block)) {
				seen.add(block);
				ro.observe(block);
			}
		});
	}

	function layoutBox(span) {
		return span.closest('p, h1, h2, h3, h4, h5, h6, li, blockquote, figcaption, td, th, div') || span.parentElement;
	}

	function scheduleLayout(force) {
		layoutForce = layoutForce || force;
		clearTimeout(layoutTimer);
		layoutTimer = setTimeout(function () {
			const forceNow = layoutForce;
			layoutForce = false;
			layout(forceNow);
		}, 80);
	}

	function layout(force) {
		let changed = false;

		hosts.forEach(function (span) {
			const box = layoutBox(span);
			const width = box ? box.clientWidth : 0;

			if (!width) {
				return;
			}

			const lineCount = countLineBoxes(span);
			const signature = width + 'x' + lineCount;
			const unresolved = lineCount > 1 && !span.classList.contains('is-underline-multiline');

			if (!force && !unresolved && signatures.get(span) === signature) {
				return;
			}

			signatures.set(span, signature);
			rebuild(span);
			changed = true;
		});

		if (changed) {
			refreshObserver();
		}
	}

	function countLineBoxes(span) {
		return withoutMask(span, function () {
			return visibleRects(span).length;
		});
	}

	function visibleRects(span) {
		return Array.prototype.filter.call(span.getClientRects(), function (rect) {
			return rect.width > 0 && rect.height > 0;
		});
	}

	function withoutMask(span, fn) {
		const props = ['mask', '-webkit-mask', 'mask-image', '-webkit-mask-image'];
		const previous = props.map(function (prop) {
			return span.style.getPropertyValue(prop);
		});

		props.forEach(function (prop) {
			span.style.setProperty(prop, 'none');
		});

		try {
			return fn();
		} finally {
			props.forEach(function (prop, index) {
				if (previous[index]) {
					span.style.setProperty(prop, previous[index]);
				} else {
					span.style.removeProperty(prop);
				}
			});
		}
	}

	function rebuild(span) {
		const drawn = span.dataset.underlineDrawn === '1';
		restore(span);
		splitUnderline(span);

		if (drawn) {
			seal(span);
		}
	}

	function restore(span) {
		if (!span.classList.contains('is-underline-multiline')) {
			return;
		}

		span.innerHTML = span.dataset.underlineSource;
		span.classList.remove('is-underline-multiline');
	}

	function splitUnderline(span) {
		withoutMask(span, function () {
			const lines = measureLines(span);

			if (commitLines(span, lines) || splitWithProbes(span)) {
				span.classList.add('is-underline-multiline');
			}
		});
	}

	function commitLines(span, lines) {
		if (lines.length < 2) {
			return false;
		}

		lines.forEach(function (line, index) {
			trimLine(line.parts, index === 0, index === lines.length - 1);
		});

		let wrapped = 0;

		for (let i = lines.length - 1; i >= 0; i--) {
			if (wrapLine(lines[i])) {
				wrapped += 1;
			}
		}

		if (wrapped < 2) {
			span.innerHTML = span.dataset.underlineSource;
			return false;
		}

		return true;
	}

	function measureLines(root) {
		const range = document.createRange();
		const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
			acceptNode: function (node) {
				return node.nodeValue ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
			},
		});
		const lines = [];
		const gap = lineBreakGap(root);
		let current = null;
		let lineTop = null;
		let node = walker.nextNode();

		while (node) {
			const value = node.nodeValue;

			for (let i = 0; i < value.length; i++) {
				range.setStart(node, i);
				range.setEnd(node, i + 1);
				const rects = range.getClientRects();
				const rect = rects.length ? rects[0] : null;
				const blank = !rect || rect.width === 0 || rect.height === 0;

				if (blank) {
					if (current) {
						appendPart(current, node, i);
					}
					continue;
				}

				if (!current || (lineTop != null && rect.top - lineTop > gap)) {
					current = { parts: [] };
					lines.push(current);
					lineTop = rect.top;
				}

				appendPart(current, node, i);
			}

			node = walker.nextNode();
		}

		return lines;
	}

	function lineBreakGap(root) {
		const style = window.getComputedStyle(root);
		const lineHeight = parseFloat(style.lineHeight);

		if (lineHeight) {
			return lineHeight * 0.5;
		}

		const fontSize = parseFloat(style.fontSize) || 16;
		return fontSize * 0.65;
	}

	function appendPart(line, node, index) {
		const parts = line.parts;
		const last = parts[parts.length - 1];

		if (last && last.node === node && last.end === index) {
			last.end = index + 1;
			return;
		}

		parts.push({ node: node, start: index, end: index + 1 });
	}

	function trimLine(parts, isFirst, isLast) {
		if (!isFirst) {
			while (parts.length) {
				const part = parts[0];
				const value = part.node.nodeValue || '';

				while (part.start < part.end && /\s/.test(value.charAt(part.start))) {
					part.start += 1;
				}

				if (part.start < part.end) {
					break;
				}

				parts.shift();
			}
		}

		if (isLast) {
			return;
		}

		while (parts.length) {
			const last = parts[parts.length - 1];
			const value = last.node.nodeValue || '';

			while (last.end > last.start && /\s/.test(value.charAt(last.end - 1))) {
				last.end -= 1;
			}

			if (last.end > last.start) {
				break;
			}

			parts.pop();
		}
	}

	function wrapLine(line) {
		const parts = line.parts;

		if (!parts.length) {
			return false;
		}

		const first = parts[0];
		const last = parts[parts.length - 1];
		const range = document.createRange();

		try {
			range.setStart(first.node, first.start);
			range.setEnd(last.node, last.end);
		} catch (error) {
			return false;
		}

		if (range.collapsed) {
			return false;
		}

		const wrapper = document.createElement('span');
		wrapper.className = 'text-underline-line';
		wrapper.style.textDecoration = 'underline';

		try {
			wrapper.appendChild(range.extractContents());
			range.insertNode(wrapper);
		} catch (error) {
			return false;
		}

		return true;
	}

	function splitWithProbes(span) {
		const textNodes = [];
		const walker = document.createTreeWalker(span, NodeFilter.SHOW_TEXT, {
			acceptNode: function (node) {
				return node.nodeValue ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
			},
		});
		let textNode = walker.nextNode();

		while (textNode) {
			textNodes.push(textNode);
			textNode = walker.nextNode();
		}

		if (!textNodes.length) {
			return false;
		}

		textNodes.forEach(function (node) {
			const value = node.nodeValue;
			const frag = document.createDocumentFragment();
			const pattern = /\s+|\S+/g;
			let match;

			while ((match = pattern.exec(value))) {
				const probe = document.createElement('span');
				probe.className = 'text-underline-probe';
				probe.textContent = match[0];
				frag.appendChild(probe);
			}

			if (node.parentNode) {
				node.parentNode.replaceChild(frag, node);
			}
		});

		const probes = Array.prototype.slice.call(span.querySelectorAll('.text-underline-probe'));
		const lines = groupProbes(probes, lineBreakGap(span));

		if (lines.length < 2) {
			unwrapProbes(span);
			return false;
		}

		for (let i = lines.length - 1; i >= 0; i--) {
			const items = trimProbes(lines[i], i === 0, i === lines.length - 1);

			if (!items.length) {
				continue;
			}

			const wrapper = document.createElement('span');
			wrapper.className = 'text-underline-line';
			wrapper.style.textDecoration = 'underline';
			items[0].parentNode.insertBefore(wrapper, items[0]);
			items.forEach(function (probe) {
				wrapper.appendChild(probe);
				probe.replaceWith(document.createTextNode(probe.textContent));
			});
		}

		unwrapProbes(span);
		span.normalize();

		if (span.querySelectorAll('.text-underline-line').length < 2) {
			span.innerHTML = span.dataset.underlineSource;
			return false;
		}

		return true;
	}

	function groupProbes(probes, gap) {
		const lines = [];
		let current = null;
		let lineTop = null;

		probes.forEach(function (probe) {
			const text = probe.textContent || '';

			if (!text) {
				return;
			}

			const space = /^\s+$/.test(text);
			const top = probe.getBoundingClientRect().top;

			if (!space && (!current || (lineTop != null && top - lineTop > gap))) {
				current = [];
				lines.push(current);
				lineTop = top;
			}

			if (!current) {
				current = [];
				lines.push(current);
				lineTop = top;
			}

			current.push(probe);
		});

		return lines;
	}

	function trimProbes(probes, isFirst, isLast) {
		const items = probes.slice();

		if (!isFirst) {
			while (items.length && /^\s+$/.test(items[0].textContent || '')) {
				items.shift();
			}
		}

		if (!isLast) {
			while (items.length && /^\s+$/.test(items[items.length - 1].textContent || '')) {
				items.pop();
			}
		}

		return items.filter(function (probe) {
			return (probe.textContent || '').length > 0;
		});
	}

	function unwrapProbes(span) {
		Array.prototype.forEach.call(span.querySelectorAll('.text-underline-probe'), function (probe) {
			probe.replaceWith(document.createTextNode(probe.textContent));
		});
		span.normalize();
	}

	function lineSpans(span) {
		return Array.prototype.slice.call(span.querySelectorAll('.text-underline-line'));
	}

	function seal(span) {
		const lines = lineSpans(span);
		const list = lines.length ? lines : [span];

		list.forEach(function (el) {
			el.style.transition = 'none';
			el.classList.add('is-underline-drawn');
		});
	}

	function refreshObserver() {
		if (reduced) {
			return;
		}

		if (!observer) {
			observer = new IntersectionObserver(
				function (entries) {
					entries.forEach(function (entry) {
						if (!entry.isIntersecting) {
							return;
						}

						observer.unobserve(entry.target);
						whenVisible(entry.target, function () {
							queueDraw(entry.target);
						});
					});
				},
				{
					threshold: 0.4,
					rootMargin: '0px 0px -8% 0px',
				},
			);
		} else {
			observer.disconnect();
		}

		hosts.forEach(function (span) {
			const lines = lineSpans(span);
			const list = lines.length ? lines : [span];

			list.forEach(function (el) {
				if (!el.classList.contains('is-underline-drawn')) {
					observer.observe(el);
				}
			});
		});
	}

	function getStaggerParent(span) {
		const reveal = span.closest('.js-reveal');

		if (reveal && reveal.parentElement) {
			return reveal.parentElement;
		}

		const block = span.closest('p, h1, h2, h3, h4, h5, h6, li, blockquote');

		if (block && block.parentElement) {
			return block.parentElement;
		}

		return span.parentElement;
	}

	function byDocumentOrder(a, b) {
		const position = a.compareDocumentPosition(b);

		if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
			return -1;
		}

		if (position & Node.DOCUMENT_POSITION_PRECEDING) {
			return 1;
		}

		return 0;
	}

	function queueDraw(span) {
		if (span.classList.contains('is-underline-drawn')) {
			return;
		}

		const parent = getStaggerParent(span);
		let batch = batches.get(parent);

		if (!batch) {
			batch = { items: [], timer: null };
			batches.set(parent, batch);
		}

		if (batch.items.indexOf(span) === -1) {
			batch.items.push(span);
		}

		clearTimeout(batch.timer);
		batch.timer = setTimeout(function () {
			const items = batch.items.slice();
			batches.delete(parent);
			items.sort(byDocumentOrder);
			let delay = 0;
			items.forEach(function (el, index) {
				if (index > 0) {
					const prev = items[index - 1];
					const samePhrase = el.classList.contains('text-underline-line')
						&& prev.classList.contains('text-underline-line')
						&& el.closest('.is-underline-multiline') === prev.closest('.is-underline-multiline');
					delay += samePhrase ? LINE_STAGGER_MS : STAGGER_MS;
				}

				const wait = delay;
				setTimeout(function () {
					drawUnderline(el);
				}, wait);
			});
		}, BATCH_MS);
	}

	function toMs(value) {
		const part = String(value).split(',')[0].trim();
		const n = parseFloat(part);

		if (!n) {
			return 0;
		}

		return part.indexOf('ms') !== -1 ? n : n * 1000;
	}

	function whenVisible(span, callback) {
		const revealEl = span.closest('.js-reveal');

		if (!revealEl) {
			callback();
			return;
		}

		waitForReveal(revealEl, callback);
	}

	function waitForReveal(el, callback) {
		let done = false;
		const finish = function () {
			if (done) {
				return;
			}
			done = true;
			callback();
		};

		const afterIn = function () {
			if (getComputedStyle(el).opacity === '1') {
				finish();
				return;
			}

			el.addEventListener('transitionend', finish, { once: true });
			const delay = toMs(getComputedStyle(el).transitionDelay);
			const duration = toMs(getComputedStyle(el).transitionDuration) || DURATION_FALLBACK;
			setTimeout(finish, delay + duration + 50);
		};

		if (el.classList.contains('is-in')) {
			afterIn();
			return;
		}

		const mo = new MutationObserver(function () {
			if (el.classList.contains('is-in')) {
				mo.disconnect();
				afterIn();
			}
		});

		mo.observe(el, { attributes: true, attributeFilter: ['class'] });
	}

	function drawUnderline(el) {
		if (el.classList.contains('is-underline-drawn')) {
			return;
		}

		const width = el.getBoundingClientRect().width;
		const duration = Math.min(0.5 + width / 500, 1.1);

		el.style.setProperty('--text-underline-duration', duration + 's');
		el.classList.add('is-underline-drawn');

		const host = el.classList.contains('text-underline-line') ? el.closest('.is-underline-multiline') : el;

		if (!host) {
			return;
		}

		const lines = lineSpans(host);
		const pending = lines.some(function (line) {
			return !line.classList.contains('is-underline-drawn');
		});

		if (!pending) {
			host.dataset.underlineDrawn = '1';
		}
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initTextUnderlines);
	} else {
		initTextUnderlines();
	}
})();
